import { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Sparkles,
  FileSearch,
  MailCheck,
  Clock,
  Workflow,
  Calculator,
  Boxes,
  ScanEye,
  Scale,
  DatabaseBackup,
  GripVertical,
  LineChart,
  ShieldCheck,
  Server,
  Activity,
  Send,
  type LucideIcon,
} from 'lucide-react';

const INSTALL_URL = 'https://online.moysklad.ru/app/#apps?id=c5652470-2668-4096-ac28-cf73b6ef6592';
const ADDWINE_URL = 'https://just.addwine.ru/project';
const SUPPORT_EMAIL = 'support@e-ion.ru';
const SUPPORT_TELEGRAM = 'https://t.me/sup_eion';

// ─── Возможности (карточки с иконками lucide-react) ─────────────────
interface Feature {
  icon: LucideIcon;
  title: string;
  text: string;
}

const FEATURES: Feature[] = [
  {
    icon: FileSearch,
    title: 'Сам узнаёт поставщика по файлу',
    text: 'По имени файла применяет нужное правило. Кинул пачку прайсов — приложение разложит их по поставщикам и обработает каждый.',
  },
  {
    icon: MailCheck,
    title: 'Автоприём из почты',
    text: 'Забирает вложение из письма по домену отправителя и создаёт оприходование. Открывать письма не нужно — всё само.',
  },
  {
    icon: Clock,
    title: 'Запуск по расписанию',
    text: 'Проверяет источники по интервалу или раз в сутки в заданное время (например, ночью) — утром остатки и цены уже обновлены.',
  },
  {
    icon: Workflow,
    title: 'Любой источник прайса',
    text: 'Файл, прямая ссылка, облачная папка (Я.Диск / Google Drive) или YML-каталог — заберём прайс откуда угодно.',
  },
  {
    icon: Calculator,
    title: 'Считает цены и пишет в карточки',
    text: 'Себестоимость, наценки, НДС — по вашей формуле. Закупочная цена и цена продажи в карточках МойСклад обновляются сами.',
  },
  {
    icon: Boxes,
    title: 'Остатки в нужном виде',
    text: 'Коробки пересчитает в штуки, остатки нескольких складов сложит в один. Цифры в МойСклад всегда корректные.',
  },
  {
    icon: ScanEye,
    title: 'Предпросмотр перед записью',
    text: 'Сначала показывает «загрузится / не найдено / пропущено», и только по кнопке создаёт документ. Никаких сюрпризов.',
  },
  {
    icon: Scale,
    title: 'Сравнение цен поставщиков',
    text: 'Один товар у разных поставщиков — видно, у кого закупка дешевле. С выгрузкой сравнения в Excel.',
  },
  {
    icon: DatabaseBackup,
    title: 'Бэкап и перенос настроек',
    text: 'Все правила выгружаются в файл и восстанавливаются одним кликом — при переустановке или переносе на другой аккаунт.',
  },
  {
    icon: GripVertical,
    title: 'Свой порядок и группы',
    text: 'Перетаскивайте правила, собирайте поставщиков в группы, включайте и выключайте одним тумблером.',
  },
  {
    icon: LineChart,
    title: 'Статистика по поставщикам',
    text: 'Кривая суммы приходов по каждому поставщику — отслеживайте динамику закупок и ловите аномалии: резкий скачок вверх сразу виден — это крупный новый приход. Наведите на точку — сумма и дата.',
  },
];

interface Project {
  name: string;
  href: string;
  tagline: string;
  year: string;
  isNew?: boolean;
}

// Свой онлайн-бизнес — проекты, которыми команда живёт каждый день (здесь рождаются задачи).
const OWN_PROJECTS: Project[] = [
  {
    name: 'addwine',
    href: 'https://addwine.ru',
    year: '2017',
    tagline: 'Интернет-магазин винных аксессуаров — флагман, с которого всё началось.',
  },
  {
    name: 'addseller',
    href: 'https://sellers.addwine.ru',
    year: '2020',
    tagline: 'Централизованная система оперативной дистрибуции товаров.',
  },
  {
    name: 'add-event',
    href: 'https://add-event.ru',
    year: '2024',
    tagline: 'Аренда премиальной винной посуды для дегустаций, корпоративов, фестивалей.',
  },
  {
    name: 'addwibe',
    href: 'https://addwibe.ru',
    year: '2025',
    tagline: 'Платформа дегустаций и онлайн-инструменты для подбора вин и бокалов.',
  },
  {
    name: 'addlanding',
    href: 'https://just.addwine.ru/project',
    year: '2023',
    tagline: 'Конструктор продающих лендингов для собственных проектов команды.',
  },
];

// Продукты для МойСклад — то, что выросло из своей работы и стало доступно всем (линейка пополняется).
const APP_PRODUCTS: Project[] = [
  {
    name: 'Умные цены',
    href: 'https://prices.e-ion.ru',
    year: '2026',
    tagline: 'Автоматизация цен в МойСклад — правила, сегменты, формулы, расписание.',
  },
  {
    name: 'Умный импорт',
    href: 'https://provider.e-ion.ru',
    year: '2026',
    tagline: 'Авто-загрузка прайсов поставщиков в МойСклад — оприходования, цены, остатки.',
  },
  {
    name: 'Умный мониторинг',
    href: 'https://monitor.e-ion.ru',
    year: '2026',
    tagline: 'Мониторинг цен конкурентов и контроль РРЦ в МойСклад — уведомления о нарушениях.',
  },
  {
    name: 'Умный прайс',
    href: 'https://price.e-ion.ru',
    year: '2026',
    tagline: 'Авто-отправка прайсов клиентам из МойСклад — правила под получателя, цены ±%, остатки, расписание.',
  },
  {
    name: 'Умные акции маркетплейсов',
    href: 'https://profit.e-ion.ru',
    year: '2026',
    tagline: 'Ваши товары в акции маркетплейсов только тогда, когда скидка укладывается в ваш порог.',
  },
  {
    name: 'RoiSklad',
    href: 'https://roisklad.e-ion.ru',
    year: '2026',
    tagline: 'Деньги на складе под контролем: неликвид, план закупа в рублях, ABC×XYZ и GMROI по данным МоегоСклада.',
  },
];

export const HomePage = (): React.JSX.Element => {
  return (
    <>
      {/* ─────────── HERO (баннер #1) ─────────── */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-gradient-to-b from-transparent via-ink-950/30 to-ink-950"
          aria-hidden
        />
        <div className="container relative pt-2 md:pt-4 pb-16 md:pb-24">
          <h1 className="sr-only">Умный импорт — авто-загрузка прайсов поставщиков и оприходования для МойСклад</h1>

          <div className="relative w-full overflow-hidden rounded-2xl border border-ink-800 bg-ink-950 shadow-2xl shadow-black/50">
            <div className="absolute inset-0 grid-bg opacity-50" aria-hidden />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-ink-950/70" aria-hidden />
            <div className="relative px-6 py-20 md:py-28 flex flex-col items-center text-center">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-medium mb-6">
                <span className="dot" />
                <span className="text-accent">Приложение для МойСклад</span>
              </div>

              <h2 className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.05] max-w-4xl">
                Умный импорт и <span className="text-accent">оприходования</span> для МойСклад
              </h2>

              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm font-mono text-ink-400">
                <span>прайсы</span>
                <span className="text-ink-600">·</span>
                <span>оприходования</span>
                <span className="text-ink-600">·</span>
                <span>цены</span>
                <span className="text-ink-600">·</span>
                <span>остатки</span>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col items-center gap-5">
            <p className="text-center text-lg md:text-xl text-ink-300 max-w-2xl">
              Сам принимает прайсы поставщиков, создаёт оприходования в МойСклад,
              считает цены и обновляет остатки.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={INSTALL_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-accent hover:bg-accent-hover px-6 py-3 text-base font-medium text-white transition-colors"
              >
                Установить из МойСклад
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#product"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-ink-700 hover:border-ink-400 px-6 py-3 text-base font-medium text-ink-100 transition-colors"
              >
                Как это работает
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── КАК РАБОТАЕТ ─────────── */}
      <section id="product" className="container py-16 md:py-24 border-t border-ink-800">
        <div className="max-w-3xl mb-12">
          <Eyebrow>Как это работает</Eyebrow>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
            Прайс поставщика → оприходование в МойСклад. Без ручной рутины.
          </h2>
          <p className="mt-4 text-ink-300 text-lg">
            Настраиваете правило поставщика один раз — дальше приложение само принимает прайс,
            находит товары, считает цены и остатки и создаёт документ.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          <Step
            num="1"
            title="Прайс приходит"
            text="Файл, почта, прямая ссылка, облачная папка или YML-каталог — приложение само забирает прайс поставщика, по расписанию или по кнопке."
          />
          <Step
            num="2"
            title="Сопоставление и расчёт"
            text="Находим товары в МойСклад по артикулу или коду, чистим ключ, считаем цены и остатки по правилу: коробки→штуки, наценки, НДС, склады в сумму."
          />
          <Step
            num="3"
            title="Оприходование и карточки"
            text="Предпросмотр — потом запись: создаём оприходование, обновляем закупку, цену продажи и поставщика в карточках. Прошлый приход снимается, остатки не задваиваются."
          />
        </div>
      </section>

      {/* ─────────── ВОЗМОЖНОСТИ ─────────── */}
      <section id="features" className="bg-ink-900 border-y border-ink-800 py-16 md:py-24">
        <div className="container">
          <div className="max-w-3xl mb-12">
            <Eyebrow>Возможности</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              Всё, чтобы прайсы загружались сами.
            </h2>
            <p className="mt-4 text-ink-300 text-lg">
              Каждая возможность родилась из реальной задачи — мы сначала решили её для себя в{' '}
              <a
                href={ADDWINE_URL}
                target="_blank"
                rel="noreferrer"
                className="text-accent hover:underline underline-offset-2"
              >
                AddWine
              </a>
              , потом открыли для всех.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <FeatureCard key={f.title} feature={f} />
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── О КОМАНДЕ ─────────── */}
      <section id="about" className="container py-16 md:py-24">
        <div className="max-w-3xl mb-14">
          <Eyebrow>О команде</Eyebrow>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
            Разрабатываем инструменты для себя — с 2017 года.
          </h2>
          <p className="mt-6 text-ink-300 text-lg">
            Команда разработчиков и операторов онлайн-бизнеса: собственная розница, B2B,
            маркетплейсы, дегустации, аренда, винные онлайн-сервисы. Ассортимент и цены в МойСклад
            ведём сами, каждый день. Поэтому каждый продукт начинался не с презентации, а с
            конкретной боли в одном из проектов — и только потом становился отдельным сервисом.
          </p>
          <p className="mt-4 text-ink-300 text-lg">
            Это разработка не «со стороны», а изнутри собственного бизнеса — с пониманием, как всё
            устроено не в теории, а в ежедневной работе. Ставка на функциональность, удобство и{' '}
            <span className="text-white">автоматизацию принятия решений</span>: рутина считается
            сама, человек занимается важным.
          </p>

          <ul className="mt-8 space-y-3 text-ink-200">
            <Bullet>
              <span className="text-white">Реальный бизнес, а не консультанты.</span> Каждое решение
              выросло из реальной задачи в одном из проектов.
            </Bullet>
            <Bullet>
              <span className="text-white">Понятно каждому.</span> Всё сделано так, чтобы разобрался
              обычный пользователь — без технических знаний и обучения.
            </Bullet>
            <Bullet>
              <span className="text-white">Работает на автомате.</span> Настроили один раз — дальше
              приложение делает рутину само, по расписанию.
            </Bullet>
            <Bullet>
              <span className="text-white">За безопасный бизнес.</span> Любое изменение можно
              проверить заранее и откатить — без необратимых сюрпризов.
            </Bullet>
            <Bullet>
              <span className="text-white">Поддержку ведём сами.</span> Отвечают те, кто делал
              продукт, а не колл-центр по скрипту.
            </Bullet>
            <Bullet>
              <span className="text-white">Растёт вместе с вами.</span> Работает и на сотне, и на
              десятках тысяч позиций — без тормозов.
            </Bullet>
            <Bullet>
              <span className="text-white">Без переплат за лишнее.</span> Платите за результат, а не
              за красивые презентации и интеграторов.
            </Bullet>
          </ul>
        </div>

        {/* Надёжность и безопасность */}
        <div className="mb-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SafetyCard
            icon={ShieldCheck}
            title="Доступ и изоляция"
            text="Доступ — только у вас и админов аккаунта. Данные аккаунтов изолированы, третьим лицам не передаём."
          />
          <SafetyCard
            icon={Server}
            title="Шифрование, серверы в РФ"
            text="Токен доступа хранится зашифрованно, передача только по защищённому соединению."
          />
          <SafetyCard
            icon={DatabaseBackup}
            title="Резервные копии"
            text="Бэкап баз каждую ночь в облако. Если что-то упадёт — восстановим за 30–60 минут."
          />
          <SafetyCard
            icon={Activity}
            title="Мониторинг доступности"
            text="Внешний сервис следит, что приложение отвечает, и сразу сообщает нам о сбоях."
          />
        </div>

        <div className="space-y-16">
          <div>
            <Eyebrow>Наш бизнес</Eyebrow>
            <h3 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2">
              Проекты, которые ведём сами
            </h3>
            <p className="text-ink-400 mb-8 max-w-2xl">
              Собственная розница, B2B, дегустации, аренда и винные онлайн-сервисы — с 2017 года.
              Здесь каждый день рождаются задачи, которые мы потом решаем продуктами.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              {OWN_PROJECTS.map((p) => (
                <ProjectCard key={p.name} project={p} />
              ))}
            </div>
          </div>

          <div>
            <Eyebrow>Продукты для МойСклад</Eyebrow>
            <h3 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2">
              Что мы открыли для других
            </h3>
            <p className="text-ink-400 mb-8 max-w-2xl">
              Инструменты автоматизации, выросшие из нашей ежедневной работы в МойСклад. Сначала
              решили задачу для себя — потом сделали продукт для всех. Линейка пополняется.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              {APP_PRODUCTS.map((p) => (
                <ProjectCard key={p.name} project={p} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── CTA ─────────── */}
      <section
        id="contacts"
        className="border-t border-ink-800 bg-gradient-to-b from-ink-900 to-ink-950"
      >
        <div className="container py-16 md:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <Eyebrow>Готовы начать?</Eyebrow>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
              Установите «Умный импорт» в МойСклад за 30 секунд.
            </h2>
            <p className="mt-6 text-ink-300 text-lg">
              Первую загрузку делаете в предпросмотре — без рисков для боевых данных. Поддержку ведём
              сами.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={INSTALL_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-accent hover:bg-accent-hover px-7 py-3.5 text-base font-medium text-white transition-colors"
              >
                Установить из МойСклад
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={SUPPORT_TELEGRAM}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-ink-700 hover:border-ink-400 px-7 py-3.5 text-base font-medium text-ink-100 transition-colors"
              >
                <Send className="h-4 w-4" /> Чат поддержки в Telegram
              </a>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-ink-700 hover:border-ink-400 px-7 py-3.5 text-base font-medium text-ink-100 transition-colors"
              >
                Написать на почту
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// ─────────── building blocks ───────────

const Eyebrow = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-accent font-medium mb-4">
    <span className="dot" />
    {children}
  </div>
);

const Step = ({
  num,
  title,
  text,
}: {
  num: string;
  title: string;
  text: string;
}): React.JSX.Element => (
  <div className="rounded-xl border border-ink-800 bg-ink-900 p-6 hover:border-ink-600 transition-colors">
    <div className="text-accent font-mono text-sm mb-3">/ {num}</div>
    <h3 className="text-xl font-semibold mb-2">{title}</h3>
    <p className="text-ink-300 text-sm leading-relaxed">{text}</p>
  </div>
);

const FeatureCard = ({ feature }: { feature: Feature }): React.JSX.Element => {
  const Icon = feature.icon;
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-6 hover:border-ink-600 transition-colors">
      <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-accent/10 text-accent">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-lg font-semibold mb-2 leading-snug">{feature.title}</h3>
      <p className="text-ink-300 text-sm leading-relaxed">{feature.text}</p>
    </div>
  );
};

const Bullet = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <li className="flex items-start gap-3">
    <CheckCircle2 className="h-5 w-5 text-accent shrink-0 mt-0.5" />
    <span>{children}</span>
  </li>
);

const SafetyCard = ({
  icon: Icon,
  title,
  text,
}: {
  icon: LucideIcon;
  title: string;
  text: string;
}): React.JSX.Element => (
  <div className="rounded-2xl border border-ink-800 bg-ink-900 p-6">
    <Icon className="h-6 w-6 text-accent mb-4" />
    <h3 className="text-lg font-semibold mb-2">{title}</h3>
    <p className="text-sm text-ink-400 leading-relaxed">{text}</p>
  </div>
);

const ProjectCard = ({ project }: { project: Project }): React.JSX.Element => {
  const url = new URL(project.href);
  const displayHost = url.host + (url.pathname !== '/' ? url.pathname : '');
  return (
    <a
      href={project.href}
      target="_blank"
      rel="noreferrer"
      className={
        project.isNew
          ? 'group relative rounded-xl border-2 border-accent bg-ink-900 p-6 hover:bg-ink-800 transition-colors'
          : 'group relative rounded-xl border border-ink-800 bg-ink-900 p-6 hover:border-ink-600 transition-colors'
      }
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Sparkles
            className={
              project.isNew
                ? 'h-4 w-4 text-accent shrink-0'
                : 'h-4 w-4 text-ink-400 shrink-0 group-hover:text-accent transition-colors'
            }
          />
          <span className="font-mono text-base text-white font-semibold">{project.name}</span>
          {project.isNew && (
            <span className="ml-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-accent text-white">
              новинка
            </span>
          )}
        </div>
        <span className="text-xs text-ink-400 font-mono shrink-0">с {project.year}</span>
      </div>
      <p className="text-sm text-ink-200 mb-4 leading-relaxed">{project.tagline}</p>
      <div className="flex items-center gap-1.5 text-xs text-ink-400 group-hover:text-accent transition-colors">
        <span className="font-mono truncate">{displayHost}</span>
        <ArrowUpRight className="h-3.5 w-3.5 shrink-0" />
      </div>
    </a>
  );
};
